import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "../data/site";
import { Lightbox } from "./Lightbox";

export const Portfolio = ({ onQuote }) => {
    const [lb, setLb] = useState({ project: null, index: null });
    const featured = PROJECTS[0];
    const others = PROJECTS.slice(1);

    const openLB = (project, i) => setLb({ project, index: i });
    const closeLB = () => setLb({ project: null, index: null });
    const prev = () =>
        setLb((s) => ({
            ...s,
            index: (s.index - 1 + s.project.gallery.length) % s.project.gallery.length,
        }));
    const next = () =>
        setLb((s) => ({ ...s, index: (s.index + 1) % s.project.gallery.length }));

    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="py-24 lg:py-36 bg-[#FAF9F6]"
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
                {/* Section heading */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
                    <div className="max-w-2xl">
                        <div className="overline text-[#4A4643] mb-6">
                            Selected work
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#1A1A1A]">
                            Recent <span className="italic text-[#8C7C6D]">case studies</span>.
                        </h2>
                    </div>
                    <Link
                        to="/work"
                        data-testid="portfolio-view-all"
                        className="btn-secondary self-start lg:self-end"
                    >
                        View all projects
                        <ArrowUpRight size={16} strokeWidth={1.5} />
                    </Link>
                </div>

                {/* Featured project — full case study */}
                <div className="grid grid-cols-12 gap-6 lg:gap-12 mb-10 lg:mb-16">
                    <div className="col-span-12 lg:col-span-4 space-y-4">
                        <div className="overline text-[#8C7C6D]">
                            01 · {featured.category} · {featured.year}
                        </div>
                        <h3 className="font-serif text-3xl lg:text-4xl text-[#1A1A1A] tracking-tight font-medium leading-tight">
                            {featured.title}
                        </h3>
                        <div className="hairline my-4" />
                        <dl className="text-[15px] font-light leading-relaxed">
                            <MetaRow label="Location" value={featured.location} />
                            <MetaRow label="Area" value={featured.area} />
                            <MetaRow label="Discipline" value={`${featured.category} — turnkey design & delivery`} />
                        </dl>
                    </div>
                    <div className="col-span-12 lg:col-span-7 lg:col-start-6">
                        <p className="text-[#4A4643] text-base lg:text-lg leading-relaxed font-light">
                            {featured.summary}
                        </p>
                        <button
                            onClick={onQuote}
                            data-testid="portfolio-brief-cta"
                            className="mt-8 btn-secondary"
                        >
                            Start a similar project
                            <ArrowUpRight size={16} strokeWidth={1.5} />
                        </button>
                    </div>
                </div>

                <motion.button
                    type="button"
                    onClick={() => openLB(featured, 0)}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    data-testid="portfolio-feature-image"
                    className="img-zoom block w-full aspect-[16/9] bg-[#F0EFEA] overflow-hidden cursor-pointer"
                >
                    <img
                        src={featured.gallery[0].src}
                        alt={featured.gallery[0].caption}
                        className="w-full h-full object-cover"
                    />
                </motion.button>

                <div className="mt-6 lg:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                    {featured.gallery.slice(1).map((img, i) => (
                        <motion.button
                            type="button"
                            key={img.src}
                            onClick={() => openLB(featured, i + 1)}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6, delay: i * 0.06 }}
                            data-testid={`portfolio-gallery-${i}`}
                            className="img-zoom block w-full aspect-[3/4] bg-[#F0EFEA] overflow-hidden cursor-pointer text-left"
                        >
                            <img
                                src={img.src}
                                alt={img.caption}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </motion.button>
                    ))}
                </div>

                {/* Other projects — teaser cards */}
                {others.length > 0 && (
                    <div className="mt-24 lg:mt-32">
                        <div className="overline text-[#4A4643] mb-8">
                            More work
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                            {others.map((p, idx) => (
                                <ProjectTeaser
                                    key={p.id}
                                    project={p}
                                    number={idx + 2}
                                    onOpen={() => openLB(p, 0)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer note */}
                <div className="mt-24 lg:mt-32 grid grid-cols-12 gap-6 lg:gap-12 items-center">
                    <div className="col-span-12 lg:col-span-8">
                        <p className="font-serif italic text-2xl lg:text-3xl text-[#1A1A1A] font-light leading-snug">
                            &ldquo;More projects — residential and modular — from our Pune and
                            Goa studios coming soon.&rdquo;
                        </p>
                    </div>
                    <div className="col-span-12 lg:col-span-4 lg:text-right">
                        <button
                            onClick={onQuote}
                            data-testid="portfolio-quote-cta"
                            className="btn-primary"
                        >
                            Discuss your project
                        </button>
                    </div>
                </div>
            </div>

            {lb.project && (
                <Lightbox
                    images={lb.project.gallery}
                    index={lb.index}
                    onClose={closeLB}
                    onPrev={prev}
                    onNext={next}
                />
            )}
        </section>
    );
};

const MetaRow = ({ label, value }) => (
    <div className="flex items-baseline gap-6 py-1.5">
        <dt className="overline text-[#8C7C6D] w-24 flex-shrink-0">{label}</dt>
        <dd className="text-[#1A1A1A]">{value}</dd>
    </div>
);

const ProjectTeaser = ({ project, number, onOpen }) => (
    <motion.article
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        data-testid={`portfolio-teaser-${project.id}`}
        className="group"
    >
        <button
            type="button"
            onClick={onOpen}
            className="img-zoom block w-full aspect-[4/3] bg-[#F0EFEA] overflow-hidden cursor-pointer"
            data-testid={`teaser-image-${project.id}`}
        >
            <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover"
                loading="lazy"
            />
        </button>
        <div className="mt-5 flex items-start justify-between gap-4">
            <div>
                <div className="overline text-[#8C7C6D]">
                    {String(number).padStart(2, "0")} · {project.category} · {project.year}
                </div>
                <h4 className="font-serif text-2xl lg:text-3xl mt-2 text-[#1A1A1A] tracking-tight font-medium">
                    {project.title}
                </h4>
                <p className="text-sm text-[#4A4643] mt-1 font-light">
                    {project.location} · {project.area}
                </p>
            </div>
            <Link
                to="/work"
                data-testid={`teaser-view-${project.id}`}
                className="overline text-[#1A1A1A] dd-link inline-flex items-center gap-1 whitespace-nowrap pt-2"
            >
                View
                <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
        </div>
    </motion.article>
);
